import { useCallback, useEffect, useRef, useState } from "react";
import { TOKEN_SERVICE_URL } from "@/consts/token-service";
export interface X402Token {
  chainId: number;
  tokenAddress: string;
  name: string;
  symbol: string;
  decimals: number;
  logoUrl: string | null;
  supportsEip2612: boolean;
  supportsEip3009: boolean;
}
interface Chain {
  name: string;
  chainId: number;
  fullName: string;
  rpcConfigured: boolean;
}
interface TokensResponse {
  chain: string;
  chainId: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    returned: number;
    hasMore: boolean;
  };
  tokens: X402Token[];
}
interface UseX402TokensResult {
  tokens: X402Token[];
  chains: Chain[];
  isLoading: boolean;
  error: string | null;
  getChainName: (chainId: number) => string | undefined;
  fetchTokenMetadata: (chainId: number, tokenAddress: string) => Promise<X402Token | null>;
}

// Cache for chains
let chainsCache: Chain[] | null = null;
let chainsCachePromise: Promise<Chain[]> | null = null;
async function fetchChains(): Promise<Chain[]> {
  if (chainsCache) return chainsCache;
  if (chainsCachePromise) return chainsCachePromise;

  // Fetch only x402-supported chains
  chainsCachePromise = fetch(`${TOKEN_SERVICE_URL}/chains?x402=true`).then(res => res.json()).then((data: Chain[]) => {
    chainsCache = data;
    return data;
  }).catch(err => {
    console.error("Failed to fetch chains:", err);
    chainsCachePromise = null;
    return [];
  });
  return chainsCachePromise;
}
export function useX402Tokens(chainId: number | undefined): UseX402TokensResult {
  const [tokens, setTokens] = useState<X402Token[]>([]);
  const [chains, setChains] = useState<Chain[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKey = useRef<string>("");

  // Fetch chains on mount
  useEffect(() => {
    fetchChains().then(setChains);
  }, []);

  // Get chain name from chainId
  const getChainName = useCallback((id: number): string | undefined => {
    const chain = chains.find(c => c.chainId === id);
    return chain?.name;
  }, [chains]);

  // Fetch tokens when chainId changes
  useEffect(() => {
    if (!chainId || chains.length === 0) {
      setTokens([]);
      return;
    }
    const chainName = getChainName(chainId);
    if (!chainName) {
      setError(`Chain ${chainId} not supported for x402 payments`);
      setTokens([]);
      return;
    }
    const fetchKey = `${chainId}`;
    if (lastFetchKey.current === fetchKey) {
      return; // Already fetched
    }
    lastFetchKey.current = fetchKey;
    setIsLoading(true);
    setError(null);
    const fetchTokens = async () => {
      try {
        // Fetch all tokens that support EIP-2612 or EIP-3009 for gasless transfers
        const response = await fetch(`${TOKEN_SERVICE_URL}/tokens/${chainName}?x402=true&limit=100`);
        if (!response.ok) {
          throw new Error("Failed to fetch tokens");
        }
        const data: TokensResponse = await response.json();
        setTokens(data.tokens);
      } catch (err) {
        console.error("Failed to fetch x402 tokens:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch tokens");
        setTokens([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTokens();
  }, [chainId, chains, getChainName]);

  // Fetch single token metadata (for custom addresses)
  const fetchTokenMetadata = useCallback(async (chainIdParam: number, tokenAddress: string): Promise<X402Token | null> => {
    const chainName_0 = getChainName(chainIdParam);
    if (!chainName_0) {
      return null;
    }
    try {
      const response_0 = await fetch(`${TOKEN_SERVICE_URL}/metadata/${chainName_0}/${tokenAddress}`);
      if (!response_0.ok) {
        return null;
      }
      const token: X402Token = await response_0.json();
      return token;
    } catch (err_0) {
      console.error("Failed to fetch token metadata:", err_0);
      return null;
    }
  }, [getChainName]);
  return {
    tokens,
    chains,
    isLoading,
    error,
    getChainName,
    fetchTokenMetadata
  };
}